import React, { useRef, useEffect } from 'react';
import { theme } from '../theme';
import { REASON_CODES } from '../constants';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { SecondaryAxisPills, neutralPillStyle } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';
import { usePaletteMode } from '../context/PaletteModeContext';
import { zoneMeta, overrideSignalBadge, widestZoneLabel } from '../services/replyEngineMeta';
import { DateRangeInput, DateRangeValue } from './DateRangeInput';
import { TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';

// ─── BentoCard ───────────────────────────────────────────
// Boundary signal: navy-tinted soft shadow (cardLifted) — static, no hover pop.
// No borders. Shadow gives separation from page bg without bento-box chrome.
// NavyKpiCard keeps its 2pt navy top-rule as the headline-tile signal — different visual register.
export const BentoCard: React.FC<{
  // ReactNode (not just string) so a loading consumer can pass a SkeletonBlock in place of the
  // title text (Task C) without BentoCard needing its own loading branch.
  title: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  headerAction?: React.ReactNode;
}> = ({ title, children, style, contentStyle, headerAction }) => {
  return (
    <div
      data-card
      style={{
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.md,
        boxShadow: theme.shadows.cardLifted,
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        paddingBottom: theme.spacing.md,
        minWidth: 0,
      }}>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 600,
          color: theme.colors.navy, fontFamily: theme.font.family,
          userSelect: 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {headerAction && (
          <div style={{ marginLeft: theme.spacing.sm, flexShrink: 0 }}>{headerAction}</div>
        )}
      </div>
      <div style={{ padding: theme.spacing.md, paddingTop: 0, overflow: 'hidden', ...contentStyle }}>{children}</div>
    </div>
  );
};

// ─── Icons ──────────────────────────────────────────────
export const PaperclipIcon: React.FC<{ size?: number; color?: string }> = ({ size = 13, color = theme.colors.navyMuted }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

export const DownloadIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Why: design dept v2 (~/Documents/xlsx2.svg) — larger text, evenodd letter cutouts, recolored white-on-navy
export const ExcelIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.384767,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796883,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.076167 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 286.95703 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.0918,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 0,102.60743 v 113.3457 c 0,6.93022 5.515604,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.3086,-5.62642 12.3086,-12.55664 V 115.91211 c -1e-5,-6.93023 -5.51563,-12.53494 -12.3086,-12.55664 z m 93.773437,24.85351 c 2.784,0 4.79883,2.01678 4.79883,4.80078 v 52.41602 c 0,4.8 2.016783,8.16015 4.800783,8.16015 h 2.40039 c 2.496,0 4.32031,2.01483 4.32031,4.79883 0,2.784 -2.68929,4.80078 -6.52929,4.80078 h -0.19141 c -8.448003,0 -14.400393,-7.39176 -14.400393,-17.75976 v -52.41602 c 0,-2.784 2.01678,-4.80078 4.80078,-4.80078 z M 138.125,149.54102 c 8.448,0 15.16782,2.87998 19.00782,7.58398 1.728,2.112 1.63123,4.51283 -0.38477,6.04883 -2.112,1.44 -5.27922,0.76736 -7.19922,-1.05664 -3.264,-3.072 -6.71923,-4.03125 -11.61523,-4.03125 -5.664,0 -10.08008,2.01606 -10.08008,5.66406 0,3.552 2.01481,5.66339 12.38281,7.77539 14.4,2.88 19.20117,7.96874 19.20117,15.55274 0,8.736 -7.10462,15.74414 -20.64062,15.74414 -8.928,0 -17.0885,-3.07288 -21.3125,-7.29688 -1.92,-2.016 -1.53613,-4.99075 0.67187,-6.71875 2.496,-2.016 5.18382,-1.53718 7.00782,0.38282 2.208,2.4 7.39204,4.99218 13.24804,4.99218 7.392,0 11.42549,-2.68734 11.52149,-6.52734 0.096,-3.84 -3.07264,-6.62522 -13.05664,-8.44922 -12.96,-2.496 -18.43164,-7.77597 -18.43164,-15.16797 0,-9.792 9.88768,-14.49609 19.67968,-14.49609 z m -101.472653,0.38477 c 1.44,0 2.68843,0.48081 3.64843,1.63281 l 15.74414,18.81445 14.11133,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26562,18.23829 c 2.88,3.36 0.86344,7.39257 -2.97656,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z m 134.400393,0 c 1.44,0 2.68844,0.48081 3.64843,1.63281 l 15.74415,18.81445 14.11132,-18.81445 c 0.96,-1.248 2.11274,-1.63281 3.55274,-1.63281 4.224,0 5.3752,4.60875 2.7832,7.96875 l -14.49609,18.9121 15.26367,18.23829 c 2.88,3.36 0.86539,7.39257 -2.97461,7.39257 -1.344,0 -2.49703,-0.28715 -3.45703,-1.53515 l -15.26367,-18.24024 -14.4961,18.24024 c -0.96,1.056 -2.11108,1.53515 -3.45508,1.53515 -3.936,0 -5.47315,-4.22398 -2.78515,-7.58398 l 14.88086,-18.62305 -15.83985,-18.7207 c -2.592,-3.072 -1.53679,-7.58398 2.78321,-7.58398 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

// Why: design dept v2 (~/Documents/pdf2.svg) — larger text, evenodd letter cutouts + counter circles for p/d
export const PdfPageIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="-2 0 354 354" style={{ fillRule: 'evenodd' as const, clipRule: 'evenodd' as const }}>
    <path d="m 65.695314,0 c -6.793,0 -12.30859,5.514594 -12.3086,12.308594 v 85.042972 l 185.796876,0.58203 c 6.79297,0.0213 12.30858,5.514594 12.30859,12.308594 v 110.44922 c 0,6.794 -5.51559,12.31055 -12.30859,12.31055 H 53.386714 v 108.95898 c 0,6.794 5.5156,12.31055 12.3086,12.31055 H 287.26757 c 6.793,0 12.3086,-5.51655 12.3086,-12.31055 V 81.998046 c -35.84776,-0.0294 -41.36682,-0.24219 -73.62696,-0.24219 -11.57089,0 -10.46484,0.98995 -10.46484,-10.07226 V 0 Z" fill="#ffffff"/>
    <path d="m 219.40234,0 -0.0273,75.162106 c 0,1.64466 0.40056,2.02149 1.96484,2.02149 l 78.04297,-0.0293 z" fill="rgba(255,255,255,0.6)"/>
    <path d="m 137.59375,148.29102 c -9.696,0 -17.08789,7.67917 -17.08789,17.95117 0,10.272 7.39189,18.04883 17.08789,18.04883 9.696,0 16.99218,-7.77683 16.99218,-18.04883 0,-10.272 -7.29618,-17.95117 -16.99218,-17.95117 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 72.697264,148.29102 c -9.696,0 -16.99219,7.77487 -16.99219,18.04687 0,10.272 7.29619,17.95313 16.99219,17.95313 9.696,0 17.08789,-7.68113 17.08789,-17.95313 0,-10.272 -7.39189,-18.04687 -17.08789,-18.04687 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
    <path d="m 0,102.50391 v 113.3457 c 0,6.93022 5.515596,12.55664 12.308594,12.55664 H 233.88086 c 6.793,0 12.30859,-5.62642 12.30859,-12.55664 V 115.8086 c -1e-5,-6.93023 -5.51562,-12.53494 -12.30859,-12.55664 z m 159.09765,15.06641 c 2.88,0 4.89649,2.01648 4.89649,4.89648 v 43.77539 c -0.096,15.168 -11.42439,26.68945 -26.40039,26.68945 -15.072,0 -26.4961,-11.52145 -26.4961,-26.68945 0,-15.168 10.56017,-26.5918 24.57617,-26.5918 7.584,0 14.11135,3.36033 18.52735,8.73633 V 122.4668 c 0,-2.88 2.01648,-4.89648 4.89648,-4.89648 z m 44.83203,0 c 2.784,0 4.89649,1.92001 4.89649,4.41601 0,2.496 -2.11249,4.41602 -4.89649,4.41602 -7.2,0 -11.90429,5.18494 -11.90429,12.96094 v 2.59179 h 9.79297 c 2.688,0 4.51171,1.72666 4.51171,4.22266 0,2.304 -1.82371,4.0332 -4.51171,4.0332 h -9.79297 v 37.43945 c 0,2.97601 -1.91989,4.89649 -5.08789,4.89649 -2.496,0 -4.70313,-1.92049 -4.70313,-4.89649 v -37.43945 h -4.12891 c -2.688,0 -4.51171,-1.7292 -4.51171,-4.0332 0,-2.496 1.82371,-4.22266 4.51171,-4.22266 h 4.12891 v -1.63281 c 0,-13.728 8.83131,-22.75195 21.69531,-22.75195 z M 72.697264,139.65039 c 15.072,1e-5 26.4961,11.5195 26.4961,26.6875 0,15.168 -10.56018,26.59375 -24.57618,26.59375 -7.584,0 -14.11134,-3.36032 -18.52734,-8.73632 v 25.91992 c 0,2.88 -2.01648,4.89453 -4.89648,4.89453 -2.88,0 -4.89649,-2.01453 -4.89649,-4.89453 v -43.77735 c 0.096,-15.168 11.42439,-26.6875 26.40039,-26.6875 z" fill="#ffffff" style={{ strokeWidth: 1.00998 }}/>
  </svg>
);

export const PinIcon: React.FC<{ size?: number; color?: string; filled?: boolean }> = ({ size = 14, color = 'currentColor', filled }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24"
    fill={filled ? color : 'none'}
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

export const CogwheelIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const LightningIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

// SpreadsheetRowIcon — grid glyph (outer rect + row/column dividers). Gates the Form→Excel
// row-copy button alongside ticket.hasFormExtraction. Matches LightningIcon family.
// Distinct from SpreadsheetIcon (which carries a download arrow): this one is a row COPY, that one
// is a batch DOWNLOAD, and the two sit on the same screen.
export const SpreadsheetRowIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="1" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="9" y1="10" x2="9" y2="20" />
  </svg>
);

// DownloadArrowIcon — tray with a single arrow into it. The action button's face when the
// form-to-excel lever is active AND tickets are selected: download exactly those.
export const DownloadArrowIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M7 11l5 5 5-5" />
    <path d="M4 20h16" />
  </svg>
);

// MassDownloadIcon — THREE stacked arrows over the same tray. Deliberately a different glyph rather
// than the single arrow with a badge: the distinction it carries is "everything on this list" versus
// "the tickets you picked", and that is the difference between a bounded action and an unbounded one.
// A clerk must be able to tell those apart before clicking, not after reading a toast.
export const MassDownloadIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3v7" /><path d="M3.5 8l2.5 3 2.5-3" />
    <path d="M12 3v9" /><path d="M9.5 10l2.5 3 2.5-3" />
    <path d="M18 3v7" /><path d="M15.5 8l2.5 3 2.5-3" />
    <path d="M3 20h18" />
  </svg>
);

// AutoIcon — wand + two sparkles. Used by AutoButton (Plan #2 "Auto" affordance).
// Monochrome stroke-line; matches LightningIcon family.
export const AutoIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="20" x2="13" y2="11" />
    <path d="M16 4 l1.2 2.8 L20 8 l-2.8 1.2 L16 12 l-1.2 -2.8 L13 8 l2.8 -1.2 Z" />
    <path d="M20 16 l0.5 1.2 L21.7 17.7 l-1.2 0.5 L20 19.4 l-0.5 -1.2 L18.3 17.7 l1.2 -0.5 Z" />
  </svg>
);

// SelectionToggleIcon — three rectangles + checkmark overlay when active. Used as the
// sibling toggle next to AutoButton.
export const SelectionToggleIcon: React.FC<{ size?: number; color?: string; active?: boolean }> = ({ size = 14, color = 'currentColor', active }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill={active ? color : 'none'}
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <polyline points="15 17 17 19 21 14" stroke={active ? '#ffffff' : color} />
  </svg>
);

// ArchiveBoxIcon — open box with downward arrow into it. Used by AutoButton on
// Antworten (archive action) when actionable.
export const ArchiveBoxIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1 -1V8" />
    <path d="M12 11v6" />
    <polyline points="9 14 12 17 15 14" />
  </svg>
);

// RestoreIcon — counterclockwise arrow. Used by AutoButton on ArchivSeite.Erledigt
// (mass unarchive). Mirrors the inline SVG used in the per-row restore button.
export const RestoreIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4v6h6" />
    <path d="M3 10a9 9 0 1 1 2.64 6.36" />
  </svg>
);

// SpreadsheetIcon — a grid with a downward arrow. Used by AutoButton on ArchivSeite for the Form-OCR
// batch .xlsx download. Grid rather than a plain download arrow so it reads as "a table leaves here",
// which is what distinguishes it from the other bulk verbs beside it.
export const SpreadsheetIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="12" rx="1" />
    <path d="M3 7h18M9 3v12" />
    <path d="M12 18v4M9 21l3 3 3-3" />
  </svg>
);

// CheckCircleIcon — checkmark inside a circle. Used by AutoButton on ArchivSeite.Liegengeblieben
// (mark stale tickets as Erledigt — Liegengeblieben → Erledigt shortcut).
export const CheckCircleIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="8 12 11 15 16 9" />
  </svg>
);

// ModeSwapIcon — Lucide refresh-cw (two ¾ arcs with arrowhead bends).
// Used on the Eingegangen KPI card to toggle StatistikSeite between active and archive modes.
export const ModeSwapIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
    <polyline points="3 3 3 8 8 8"/>
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
    <polyline points="21 21 21 16 16 16"/>
  </svg>
);

// SortDirectionIcon — two vertical arrows (up + down). The active direction is
// drawn at full opacity; the inactive arrow is dimmed to 0.35 so the current
// direction is immediately legible. Pass `dir` to control which arm is active.
export const SortDirectionIcon: React.FC<{ size?: number; color?: string; dir?: 'desc' | 'asc' }> = ({
  size = 14, color = 'currentColor', dir = 'desc',
}) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Up arrow (left) — active when asc */}
    <g opacity={dir === 'asc' ? 1 : 0.35}>
      <line x1="7" y1="4" x2="7" y2="20" />
      <polyline points="3 8 7 4 11 8" />
    </g>
    {/* Down arrow (right) — active when desc */}
    <g opacity={dir === 'desc' ? 1 : 0.35}>
      <line x1="17" y1="4" x2="17" y2="20" />
      <polyline points="13 16 17 20 21 16" />
    </g>
  </svg>
);

export const CheckIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const CrossIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// KopiertIcon — clipboard with check overlay. Used by the "Kopiert" badge (copy-approval affordance).
export const KopiertIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="4" width="12" height="14" rx="2" />
    <path d="M16 4V3a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v1" />
    <path d="M5 8v11a2 2 0 0 0 2 2h7" />
    <path d="M11 11.5l2 2 4-4" />
  </svg>
);

// ReanalysisIcon — single clockwise circular arrow. Used by the reanalysis-count (↻N) badge.
export const ReanalysisIcon: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 3v5h-5" />
  </svg>
);

export const TrashIcon: React.FC<{ size?: number; color?: string }> = ({ size = 13, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

export const ThumbUpIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

// Maximize (⤢): two outward corner arrows.
export const ExpandIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2.5h4v4" /><path d="M13.5 2.5 9 7" />
    <path d="M6.5 13.5h-4v-4" /><path d="M2.5 13.5 7 9" />
  </svg>
);

// Restore from overlay (⤡): two inward corner arrows.
export const CollapseIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13.5 6.5h-4v-4" /><path d="M14 2 9.5 6.5" />
    <path d="M2.5 9.5h4v4" /><path d="M2 14l4.5-4.5" />
  </svg>
);

// Pop-out (⧉): window with an arrow leaving through the top-right.
export const PopoutIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 3H3.5A1.5 1.5 0 0 0 2 4.5v8A1.5 1.5 0 0 0 3.5 14h8a1.5 1.5 0 0 0 1.5-1.5V9" />
    <path d="M9.5 2.5H13.5V6.5" /><path d="M13.5 2.5 7.5 8.5" />
  </svg>
);

// The 24px ghost header button both detail hosts inlined for ✕ — single home now.
export const HeaderIconButton: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    title={label}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, padding: 0, cursor: 'pointer',
      background: 'transparent', border: 'none', borderRadius: theme.borderRadius.sm,
      color: theme.colors.navyMuted, outline: 'none',
    }}
  >
    {children}
  </button>
);

// ─── ChevronToggleButton ─────────────────────────────────
// Shared expand/collapse chevron chrome (circle + border + soft shadow), extracted from the
// Phase-0 Häufigste-Grund-Codes chevron so the ChatbotAnalyticsCard and the H-G-C card share
// one control. Rotation (180°) is driven by `open`; caller positions it via `style`.
export const ChevronToggleButton: React.FC<{
  open: boolean; onClick: () => void; ariaLabel: string; ariaControls?: string;
  orientation?: 'vertical' | 'horizontal'; style?: React.CSSProperties;
}> = ({ open, onClick, ariaLabel, ariaControls, orientation = 'vertical', style }) => {
  // vertical: down ⌄ (closed) ↔ up ⌃ (open). horizontal: right ▶ (closed) ↔ left ◀ (open),
  // for the sideways-expanding analytics card. Base rotation orients the down-chevron; `open` adds 180°.
  const base = orientation === 'horizontal' ? -90 : 0;
  return (
    <button type="button" onClick={onClick} aria-expanded={open} aria-controls={ariaControls} aria-label={ariaLabel}
      style={{
        width: 26, height: 26, borderRadius: '50%', background: theme.colors.white,
        border: `1px solid ${theme.colors.borderLight}`, boxShadow: '0 2px 6px rgba(0,13,102,0.14)',
        cursor: 'pointer', color: theme.colors.navy, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, ...style,
      }}>
      <span style={{ display: 'inline-flex', transform: `rotate(${base + (open ? 180 : 0)}deg)`, transition: 'transform 0.42s cubic-bezier(0.4,0,0.2,1)' }}>
        <ChevronDownIcon size={14} />
      </span>
    </button>
  );
};

// ─── Spinner ─────────────────────────────────────────────
// Navy ring with one transparent quadrant, rotates via existing stx-spin keyframe.
// Use `label` for inline status; pass `block` for centered full-area placement.
export const Spinner: React.FC<{
  size?: number;
  label?: string;
  block?: boolean;
  color?: string;
}> = ({ size = 28, label, block, color = theme.colors.navy }) => {
  const ring = (
    <span
      aria-label="Lädt"
      style={{
        display: 'inline-block', width: size, height: size,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        animation: 'stx-spin 0.9s linear infinite',
        flexShrink: 0,
      }}
    />
  );
  if (!label && !block) return ring;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      flexDirection: label ? 'column' : 'row',
      ...(block ? { padding: theme.spacing.lg, minHeight: 120 } : {}),
    }}>
      {ring}
      {label && (
        <span style={{
          fontSize: 13, color: theme.colors.navyMid, fontFamily: theme.font.family,
          fontWeight: 500, letterSpacing: 0.2,
        }}>{label}</span>
      )}
    </div>
  );
};

// ─── NavyKpiCard ─────────────────────────────────────────
export const NavyKpiCard: React.FC<{
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  // Used by StatistikSeite Eingegangen card to render the stats-mode swap toggle.
  headerAction?: React.ReactNode;
  // Gray-out the card surface (e.g. archive-mode treatment on StatistikSeite).
  muted?: boolean;
  // Inline style applied to the title span — used by StatistikSeite to cross-fade titles on mode swap.
  titleStyle?: React.CSSProperties;
}> = ({ title, children, style, headerAction, muted, titleStyle }) => {
  const { mode: palette } = usePaletteMode();
  const arch = theme.colors.mode.archived[palette];
  return (
  <div data-card style={{
    // Muted = archived card surface — light/dark per palette preference, so title flips navy/white.
    // theme.colors.neutral stays defined (sentiment Neutral slice still uses it).
    backgroundColor: muted ? arch.card : theme.colors.navy,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.card,
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden', minWidth: 0,
    // Morph+ recolor — matches DistributionHero mode swap.
    transition: 'background-color 0.58s cubic-bezier(0.4,0,0.2,1)',
    ...style,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', flexShrink: 0,
      padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
      paddingBottom: headerAction ? theme.spacing.sm : theme.spacing.md,
    }}>
      <span style={{
        flex: 1, fontSize: 14, fontWeight: 600, fontFamily: theme.font.family,
        color: muted ? arch.text : theme.colors.white, userSelect: 'none',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        ...titleStyle,
      }}>
        {title}
      </span>
      {headerAction && (
        <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
          {headerAction}
        </div>
      )}
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, justifyContent: 'center', padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>{children}</div>
  </div>
  );
};

// ─── TriStateControl ────────────────────────────────────
export const TriStateControl: React.FC<{
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md';
}> = ({ options, selected, onChange, size = 'sm' }) => {
  const isSm = size === 'sm';
  return (
    <div style={{
      display: 'inline-flex', borderRadius: theme.borderRadius.sm,
      border: `1px solid ${theme.colors.borderLight}`, overflow: 'hidden',
    }}>
      {options.map(opt => {
        const isActive = opt.value === selected;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              border: 'none', cursor: 'pointer',
              padding: isSm ? '2px 8px' : '4px 12px',
              fontSize: isSm ? 11 : 12,
              fontWeight: 500,
              fontFamily: theme.font.family,
              color: isActive ? theme.colors.white : theme.colors.navyMid,
              backgroundColor: isActive ? theme.colors.navy : theme.colors.white,
              transition: 'background-color 0.15s ease, color 0.15s ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// Why: plain-text fields that are NOT email bodies (the attachment digest) must not go through the
// thread-cutting normalizer below — a digest that happens to mention "Von: … Gesendet: …" would be
// truncated by escapeHtmlText's thread-boundary logic. This is the pure escape step only.
// Why the full 5-char set (& < > " '): a near-miss on this branch (task-19/task-21) found a
// caller that builds an HTML attribute (href="...") from this output — `&`/`<`/`>` alone leave
// `"` and `'` free to break out of an attribute and inject markup. A helper whose safety depends
// on the caller already knowing it's attribute-unsafe is a footgun, so the escape set is widened
// here rather than left to each call site to compensate for. Single pass over one map — every
// match is replaced exactly once, so there is no re-escaping hazard to order around.
const escapeMap: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' };

export const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, c => escapeMap[c]);

// Why: plain text (from StripHtmlToPlainText) set via dangerouslySetInnerHTML needs cleanup:
// 1. Strip Outlook safety banner (plain text remnant, can repeat in threaded emails)
// 2. Strip <mailto:>/<https:> URL remnants left by MimeKit in .eml TextBody
// 3. Truncate at first email thread boundary (CS rep reads thread in Outlook)
// 4. Strip mobile/client email footers ("Gesendet mit/von...", "Sent from...")
// 5. Collapse whitespace-only blank lines (Outlook HTML → spaces/nbsp between \n)
// 6. HTML-escape < > & so forwarding headers aren't parsed as HTML elements
// Must run BEFORE styleRedactionTokens so pill <span>s are the only HTML in the output.
export const escapeHtmlText = (s: string): string => {
  // Why: .msg BodyText from Outlook uses \r\n; \r breaks whitespace-collapse regex
  let t = s.replace(/\r\n?/g, '\n');
  // Why: Outlook safety banner includes trailing URL remnant on same line
  t = t.replace(/Sie erhalten nicht häufig E-Mails von[\s\S]*?(?:Erfahren Sie, warum dies wichtig ist|LearnAboutSenderIdentification|SafetyTips)[^\n]*\s*/g, '');
  t = t.replace(/<(?:mailto|https?):.*?>/g, '');
  // Why: only show first message — thread markers: inline forward, Outlook block, web.de format
  // Why: PII redaction replaces dates/names, so markers must match [REDACTED_*] tokens too
  const threadMarkers = [
    /\nAm (?:\d{2}\.\d{2}\.\d{2,4}|\[REDACTED_DATE\]) um \d{2}:\d{2} schrieb/,
    /\n\s*(?:Von:|From:)[\s\S]{0,200}?\n(?:Gesendet|Sent):/,
    /\n(?:Gesendet|Sent):\n[A-Z]/,
    /\n\[REDACTED_[A-Z]+\]\s*<?\[REDACTED_EMAIL\]>?\n(?:Gesendet|Sent):/,
  ];
  let cut = t.length;
  for (const re of threadMarkers) {
    const m = re.exec(t);
    if (m && m.index < cut) cut = m.index;
  }
  t = t.substring(0, cut);
  t = t.replace(/^(?:Gesendet (?:mit |von (?:meinem? |Outlook )).+|Sent from .+)\s*$/gm, '');
  t = t.replace(/\n([ \t\u00a0]*\n){2,}/g, '\n\n');
  t = t.trim();
  return escapeHtml(t);
};

export const isHtml = (text: string): boolean =>
  /<(html|div|p|br|span)\b/i.test(text);

// ─── linkifyUrls ────────────────────────────────────────
// Wraps http(s):// and www. URLs found in already-HTML-escaped text into anchor tags.
// Decodes &amp; back to & only inside the href attribute. Plain rendering uses the matched (escaped) form.
// Run AFTER escapeHtmlText, BEFORE styleRedactionTokens.
const URL_REGEX = /\b(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/g;
export const linkifyUrls = (html: string): string =>
  html.replace(URL_REGEX, (match) => {
    let trimmed = match;
    let trailing = '';
    while (trimmed.length > 0 && /[.,;:!?)]/.test(trimmed.slice(-1))) {
      trailing = trimmed.slice(-1) + trailing;
      trimmed = trimmed.slice(0, -1);
    }
    // Why the trailing .replace(/"/g, '&quot;') is kept though now redundant (task-21): the
    // upstream contract (escapeHtml runs before linkifyUrls, per the module comment above) means
    // no raw " can reach this point anymore — escapeHtml already turned it into &quot;. This is
    // deliberate belt-and-braces for that upstream contract, not dead code: if a future caller
    // ever invokes linkifyUrls on non-pre-escaped text, this still stops attribute breakout on
    // its own. Do not remove without re-verifying the upstream guarantee still holds everywhere.
    const href = (trimmed.startsWith('www.') ? 'https://' + trimmed : trimmed)
      .replace(/&amp;/g, '&').replace(/"/g, '&quot;');
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#000d66;text-decoration:underline">${trimmed}</a>${trailing}`;
  });

// ─── styleRedactionTokens ───────────────────────────────
// Replaces [REDACTED_*] tokens with styled pill spans.
// Must run AFTER DOMPurify.sanitize() — DOMPurify would strip injected spans.

// Maps the BE's [REDACTED_X] type slug → key in personal_data_json.
const TYPE_TO_PD_KEY: Record<string, string> = {
  EMAIL: 'emails', IBAN: 'ibans', PHONE: 'phoneNumbers',
  NAME: 'names', DATE: 'dates', PLZ: 'postalCodes',
};

// Reveal context for click-to-reveal (UploadsSeite pre-process only — see pii-reveal-defensibility.md).
// When `revealCtx` is undefined, behavior is identical to the original redaction-only render.
export interface RevealContext {
  personalData: Record<string, any>;     // keys: emails, ibans, phoneNumbers, names, dates, postalCodes
  revealed: Set<string>;                  // membership key: `${TYPE}:${ordinal}` (0-based)
}

export const styleRedactionTokens = (html: string, revealCtx?: RevealContext): string => {
  // Per-render ordinal counter so the Nth [REDACTED_NAME] maps to personal_data_json.names[N-1]
  const ordinals: Record<string, number> = {};
  return html.replace(
    /\[REDACTED_(EMAIL|IBAN|PHONE|NAME|DATE|PLZ)\]/g,
    (match, type: string) => {
      const idx = (ordinals[type] = (ordinals[type] ?? -1) + 1);
      const escaped = match.replace(/[<>&"']/g, c => escapeMap[c]);

      // Resolve the source value if a reveal context is provided.
      let revealedValue: string | null = null;
      if (revealCtx) {
        const pdKey = TYPE_TO_PD_KEY[type];
        const arr = pdKey ? revealCtx.personalData[pdKey] : undefined;
        if (Array.isArray(arr) && idx < arr.length && typeof arr[idx] === 'string') {
          revealedValue = arr[idx];
        }
      }
      const isRevealed = !!revealedValue && revealCtx?.revealed.has(`${type}:${idx}`);
      const cursor = revealCtx && revealedValue ? 'pointer' : 'default';
      const display = isRevealed
        ? revealedValue!.replace(/[<>&"']/g, c => escapeMap[c])
        : escaped;
      const bg = isRevealed ? '#fff7e6' : theme.colors.bgLight;       // soft amber when revealed → "you're seeing original"
      const border = isRevealed ? theme.colors.accentOrange : theme.colors.borderLight;
      const color = isRevealed ? '#7a3a00' : theme.colors.navyMid;

      const dataAttrs = revealCtx && revealedValue
        ? ` data-pii-type="${type}" data-pii-index="${idx}"`
        : '';
      return `<span${dataAttrs} style="background:${bg};border:1px solid ${border};color:${color};padding:1px 6px;border-radius:3px;font-size:11px;font-weight:500;white-space:nowrap;cursor:${cursor}">${display}</span>`;
    }
  );
};

// ─── PanelSection ───────────────────────────────────────
export const PanelSection: React.FC<{
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  headerAction?: React.ReactNode;
}> = ({ title, children, collapsible, expanded, onToggle, headerAction }) => (
  <div style={{ marginBottom: theme.spacing.lg }}>
    <h3 style={{
      margin: `0 0 ${theme.spacing.sm}px 0`,
      display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
    }}>
      <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!!expanded}
            style={{
              background: 'none', border: 'none', padding: 0, margin: 0,
              font: 'inherit', color: 'inherit', cursor: 'pointer',
              userSelect: 'none', display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
            }}
          >
            {title}
            <span style={{ fontSize: 10, color: theme.colors.accentOrange }}>
              {expanded ? '\u25B2' : '\u25BC'}
            </span>
          </button>
        ) : title}
      </span>
      {headerAction && (
        <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: theme.spacing.xs }}>
          {headerAction}
        </div>
      )}
    </h3>
    {(!collapsible || expanded) && children}
  </div>
);

// ─── SkeletonBlock ──────────────────────────────────────
// <purpose>Shared shimmer placeholder for vocab-dependent chrome (Task C loading posture) — a
// consumer reading cm.configLoading renders this in place of any vocab-derived text/tiles
// while /config is still resolving.</purpose>
// <constraints>width/height are CALLER-SUPPLIED to match the real content's footprint exactly
// (UI Layout Stability Rule — nothing may shift when the real content swaps in). NEVER renders
// text: a raw `{category.…}` placeholder string is worse than an empty box, so this component
// has no text prop at all, only dimensions. The shimmer animation itself lives in index.css
// (.stx-skeleton) — prefers-reduced-motion already zeroes it globally, no local escape hatch
// needed here.</constraints>
export const SkeletonBlock: React.FC<{ width?: number | string; height: number | string; borderRadius?: number; style?: React.CSSProperties }> = ({ width = '100%', height, borderRadius = 4, style }) => (
  <div data-testid="skeleton-shimmer" aria-hidden="true" className="stx-skeleton" style={{ width, height, borderRadius, ...style }} />
);

// ─── CategoryVocabularyMissingNotice ────────────────────
// <purpose>Shared empty-state REPLACEMENT for stats surfaces when cm.vocabularyMissing is true —
// a KNOWN role (config resolved fine, no global RoleConfigWarning) whose tenant has neither a
// classification schema nor vocabulary loaded yet (e.g. freshly-provisioned VNB before taxonomy
// seeding). Without this, categoryKeys=[] renders the surface's generic "Keine Daten" text,
// indistinguishable from a genuinely empty dataset — the silent-empty state the placeholder floor
// forbids. Same shape (minHeight, centered, textSecondary) as each surface's normal empty state so
// swapping this in never shifts layout (UI Layout Stability Rule) — only the text differs.</purpose>
export const CategoryVocabularyMissingNotice: React.FC<{ minHeight?: number }> = ({ minHeight = 120 }) => (
  <div style={{
    minHeight, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: theme.colors.textSecondary, fontSize: 13, textAlign: 'center', padding: '0 12px',
  }}>
    Rollenkonfiguration unvollständig — keine Klassifizierungsdaten verfügbar.
  </div>
);

// ─── CategoryBadge ──────────────────────────────────────
export const CategoryBadge: React.FC<{ category: string; variant?: 'solid' | 'ghost'; style?: React.CSSProperties }> = ({ category, variant = 'solid', style }) => {
  const cm = useCategoryMeta();
  const ghost = variant === 'ghost';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: ghost ? theme.colors.navy : theme.colors.white,
      backgroundColor: ghost ? theme.colors.white : cm.color(category),
      borderRadius: theme.borderRadius.sm, padding: '2px 6px',
      display: 'inline-block',
      ...style,
    }}>
      {cm.categoryLabelsFromData ? cm.label(category) : category}
    </span>
  );
};

// D3 reply-engine hand-off zone chip (GRÜN/GELB/ROT — exactly three by doctrine; suppression
// is a reason, never a fourth zone). Fail-open: unknown zone strings render raw + neutral.
// InfoTooltip carries the zone legend so the chip is self-explaining without a legend panel.
//
// zone is null/undefined when the tenant's HANDOFF_ZONES lever is off — the envelope omits the
// key rather than shipping null. The honest render is NO chip, but the three call sites place
// this in a flex row where a disappearing chip would shift every sibling (UI Layout Stability
// Rule) — so absent renders the SAME span, same box, just invisible: visibility:hidden +
// aria-hidden, holding the widest doctrine label's width rather than the old neutral '—' text.
export const ZoneChip: React.FC<{ zone: string | null | undefined; style?: React.CSSProperties }> = ({ zone, style }) => {
  if (zone === null || zone === undefined) {
    return (
      <span aria-hidden="true" style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
        border: '1px solid transparent',
        borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
        display: 'inline-block', visibility: 'hidden',
        ...style,
      }}>
        {widestZoneLabel()}
      </span>
    );
  }
  const meta = zoneMeta(zone);
  // Fix wave M4: the legend rides the shared InfoTooltip, not native title= — same span, same
  // box, so the invisible-placeholder branch above still holds the identical width. A zone whose
  // meta carries no legend (the fail-open raw-string render) gets the bare chip: an empty tooltip
  // would add a focus stop that says nothing.
  const chip = (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      color: meta.fg, backgroundColor: meta.bg, border: `1px solid ${meta.border}`,
      borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
      display: 'inline-block',
      ...style,
    }}>
      {meta.label}
    </span>
  );
  return meta.legend ? <InfoTooltip text={meta.legend}>{chip}</InfoTooltip> : chip;
};

// Neutral GELB review-tier chip — same visual class as ZoneChip's Gelb variant (fg/bg/border
// tokens, pill shape) but for flagging content that needs a human look independent of the D3
// hand-off zone (e.g. a generative-answer segment). theme.colors.warning is the K4-exclusive
// orange and must NEVER be used here — this chip is keyed to theme.colors.zone.gelb instead.
// `hover` (fix wave M4, renamed from `title`): the explanation rides the shared InfoTooltip, not
// a native title= attribute, so the prop no longer names an HTML attribute this chip does not
// emit. Omitted ⇒ the bare chip, with no tooltip and no focus stop.
export const ReviewChip: React.FC<{ label: string; hover?: string; style?: React.CSSProperties }> = ({ label, hover, style }) => {
  const z = theme.colors.zone.gelb;
  const chip = (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      color: z.fg, backgroundColor: z.bg, border: `1px solid ${z.border}`,
      borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
      display: 'inline-block',
      ...style,
    }}>
      {label}
    </span>
  );
  return hover ? <InfoTooltip text={hover}>{chip}</InfoTooltip> : chip;
};

// Shared row-date formatter — DD.MM.YY (de-DE). Used by TicketRowMeta's default row 3 and by
// callers that render the date themselves (Archiv's compact line 1).
export const formatRowDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

// ─── TicketRowMeta ──────────────────────────────────────
// The tenant's inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE), applied to the AGGREGATE
// the backend measured rather than to individual attachment rows — every list-row paperclip site
// (TicketRowMeta, UploadsSeite, DashboardSeite) has only hasAttachments + documentAttachmentCount to
// work from, never the per-attachment rows the detail pane's countAttachments (attachmentOcrStatus.ts)
// consumes. One function for all three sites so the rule can't drift between them the way the three
// paperclip glyphs already had. `?? (hasAttachments ? 1 : 0)` covers both a bundle running against a
// backend that doesn't serve the count yet AND a row stamped before the count existed — both read as
// "keep today's glyph", never as an invented absence. The deliberate, documented consequence: a
// pre-stamp row's documentAttachmentCount (when the field DOES exist on an old row) equals its total
// attachment count, inline images included — so on a flag-OFF tenant that legacy row keeps its
// paperclip even though its detail pane already says "Keine Dokumente beigefügt". Fail-open by
// design; decays as those tickets archive. Do not "fix" this into a disappearing paperclip.
export const shouldShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
  attachmentsCountInline: boolean,
): boolean => attachmentsCountInline
  ? !!ticket.hasAttachments
  : (ticket.documentAttachmentCount ?? (ticket.hasAttachments ? 1 : 0)) > 0;

// The SUPERSET of shouldShowAttachmentGlyph: true for every row that could still end up with a
// paperclip under EITHER posture, false for rows no counting rule can ever give one. It exists to
// scope the reservation below — a row with no attachments at all must reserve NOTHING, because
// reserving width it will never use makes the settle drop that width plus its flex gap, which is
// the very content shift the slot was added to prevent, on the majority of rows.
export const mayShowAttachmentGlyph = (
  ticket: { hasAttachments?: boolean; documentAttachmentCount?: number },
): boolean => !!ticket.hasAttachments || (ticket.documentAttachmentCount ?? 0) > 0;

// Reserves the paperclip's footprint during the pre-settle config-loading window (cm.configLoading)
// so nothing shifts when shouldShowAttachmentGlyph's verdict becomes trustworthy — painting a glyph
// and correcting it on settle is a content shift (Layout-Stability). Render it ONLY behind
// mayShowAttachmentGlyph. `size` is caller-supplied and must equal the PaperclipIcon size that site
// actually paints (13 in TicketRowMeta, 12 in the two page-level list rows) — a slot wider than its
// glyph shifts on settle in exactly the direction it was meant to prevent.
export const AttachmentGlyphSlot: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <span data-testid="attachment-glyph-slot" style={{ width: size, flexShrink: 0 }} aria-hidden="true" />
);

// <purpose>Rows 2+3 of a ticket-list row, shared across Antworten/Uploads/Archiv/Dashboard so the
// layout can't drift. Row 2 (l→r): category badge (ONLY when rowLayout='strip_label') · pending
// chip (mid-flight status, no category yet) · Anliegen/reason-code badges · +N · attachment
// (right). Row 3: DD.MM.YY (left) · status group ↻N · Kopiert · bearbeitet · Eskalationsstufe
// (right).</purpose>
// <constraints>Right groups use marginLeft:auto — no fixed widths, no row jump (Layout-Stability).
// dateIso + maxSecondaryAxisVisible are caller-supplied (visible timestamp + per-page pill cap differ).
// strip_abbr (Kürzel im Streifen) is rendered by the caller inside the colour strip, not here.
// compact (Archiv ledger): ONE meta line, category always a badge (no strip there), date omitted
// (caller renders it on the subject line via formatRowDate).
// Pending chip: the BE bucket boundary now lets 'categorizing'/'categorized'/'generating_suggestion'
// tickets (category NULL, no suggestion yet) into the same buckets that already carry 'reanalyzing'
// — TRANSIENT_STATUSES (useTicketDetailRefresh, the canonical set the detail-panel poller already
// keys off) is reused here rather than a second hand-rolled status list. Shown ONLY when there's no
// category yet, so an in-progress reanalysis (which keeps its prior category) renders exactly as
// before — byte-identical, no new chip — while the three pre-category statuses get a "Wird
// verarbeitet…" placeholder instead of empty space where the category badge would be.</constraints>
export const TicketRowMeta: React.FC<{
  ticket: {
    primaryAxis?: string;
    // Drives the pending chip only — absent/unrecognized status is simply never pending
    // (fail-open, same posture as every other optional field here).
    status?: string;
    secondaryAxis?: string[];
    hasAttachments?: boolean;
    // Documents only, per the ingest stamp — see shouldShowAttachmentGlyph above for how this and
    // hasAttachments combine under the tenant's counting rule.
    documentAttachmentCount?: number;
    reanalysisCount?: number;
    copyApprovedAt?: string | null;
    edited?: boolean;
    eskalationsstufe?: number | null;
    // E2 zone-sorted inbox — clerk-instruction badge (additive — absent on pre-engine/gMSB rows,
    // layout unchanged). The zone chip itself lives on detail surfaces only, not here.
    statusKurz?: string | null;
    // Override SIGNAL id off the latest wim triage record (additive — absent on non-override rows).
    overrideVerdict?: string | null;
  };
  dateIso?: string | null;
  maxSecondaryAxisVisible: number;
  compact?: boolean;
  // Tenant-wide inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE). Defaults true so every
  // existing test double / call site written before this prop existed keeps today's exact glyph
  // behaviour (hasAttachments alone) rather than silently flipping posture.
  attachmentsCountInline?: boolean;
}> = ({ ticket: t, dateIso, maxSecondaryAxisVisible, compact = false, attachmentsCountInline = true }) => {
  const cm = useCategoryMeta();
  // These are NOT secondary-axis pills (pending/reanalysis/kopiert/bearbeitet/statusKurz below all
  // spread this) — they share SecondaryAxisPills' 'row' shape exactly, so they draw from the same
  // factory rather than carrying a second hand-rolled copy of it.
  const pill: React.CSSProperties = neutralPillStyle('row');
  const secondaryAxis = t.secondaryAxis ?? [];
  const eskN = t.eskalationsstufe && t.eskalationsstufe >= 1
    ? (Math.min(Math.max(t.eskalationsstufe, 1), 3) as 1 | 2 | 3) : null;
  const esk = eskN ? theme.colors.eskalation[`s${eskN}` as 's1' | 's2' | 's3'] : null;
  // Category as a badge — always in compact (Archiv has no colour strip); in default only when
  // the operator chose strip_label (strip_abbr renders the abbr in the caller's strip instead).
  const categoryBadge = (compact || cm.rowLayout === 'strip_label') && t.primaryAxis && (cm.categoryLabelsFromData ? (
    <span style={{ ...pill, fontWeight: 600, color: theme.colors.white, backgroundColor: cm.color(t.primaryAxis), border: 'none', display: 'inline-flex', alignItems: 'center', lineHeight: '14px' }}>
      {cm.label(t.primaryAxis)}
    </span>
  ) : (
    <CategoryBadge category={t.primaryAxis} style={{ fontSize: 10, padding: '1px 6px', flexShrink: 0 }} />
  ));
  // Pending chip — see <constraints>. Only when there's genuinely no category yet; a ticket that
  // already has one (e.g. mid-reanalysis) keeps its existing categoryBadge treatment untouched.
  const pendingChip = !t.primaryAxis && !!t.status && TRANSIENT_STATUSES.has(t.status) && (
    <span data-testid="pending-chip" style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Spinner size={10} />Wird verarbeitet…
    </span>
  );
  // The zone (GRÜN/GELB/ROT) chip lives on detail surfaces only (KI-Vorschlag, PopoutShell slim
  // header — via the ZoneChip component below) — one visual home for the zone signal, not
  // duplicated onto every list row. statusKurz = clerk-instruction badge, stays on the list row.
  const statusKurzPill = t.statusKurz && (
    <InfoTooltip text={t.statusKurz}>
      <span style={{ ...pill, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
        {t.statusKurz}
      </span>
    </InfoTooltip>
  );
  // Override SIGNAL pill (role-vocabulary round §4/§5): a row reading "H1 and nothing else" must be
  // distinguishable from a genuine H1 here, the surface people scan fastest. Same shape, same
  // in-play rule (active rows; the compact Archiv branch omits it) as statusKurz; the face and the
  // honest hover come from the one override-id → German home (replyEngineMeta).
  const overrideSignal = t.overrideVerdict ? overrideSignalBadge(t.overrideVerdict) : null;
  const overrideSignalPill = overrideSignal && (
    <InfoTooltip text={overrideSignal.hover}>
      <span data-testid="override-signal-pill" style={{ ...pill, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
        {overrideSignal.face}
      </span>
    </InfoTooltip>
  );
  // THE secondary axis pill, ported to SecondaryAxisPills — see its own header for the ruling
  // (de-slugged-lowercase face, standard colouring, full proper-case hover, never abbreviated, no
  // role exceptions). This row used to run a three-tier fallback here that let a vocabulary
  // delivery jump the face straight from the de-slugged id to the full German label (the divergence
  // formerly logged as D-6) — that transitional behaviour ends here; the component always renders
  // the de-slugged face regardless of what the tenant's data source is.
  // TicketRowMeta, DashboardSeite, TicketDetail, StatistikSeite and TriagePanel now share this one
  // component. UploadsSeite still calls formatSecondaryAxisCode/getReasonCodeLabel directly for its own
  // INTERACTIVE (removable) reason-code chip — not this migration's scope, same boundary as
  // FilterPanel's filter bubble below (own markup/active-state, resolution accessors only).
  // ChatbotTestSeite's two reason-code chips are plain (non-interactive) display and simply were
  // not in this task's surface list — a real residual, not a deliberate boundary like the two above.
  const secondaryAxisPills = <SecondaryAxisPills codes={secondaryAxis} maxVisible={maxSecondaryAxisVisible} variant="row" />;
  // Reserve only for rows that could get a glyph (mayShowAttachmentGlyph) — a row with no
  // attachments has nothing to reserve for, and giving it a slot would make settle collapse the
  // width AND its flex gap on the majority of rows.
  const attachment = mayShowAttachmentGlyph(t) && (
    cm.configLoading ? <AttachmentGlyphSlot size={13} /> : shouldShowAttachmentGlyph(t, attachmentsCountInline) && (
      <span title="Anhang" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
        <PaperclipIcon size={13} color={theme.colors.navyMuted} />
      </span>
    )
  );
  const statusGroup = (
    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {t.reanalysisCount != null && t.reanalysisCount > 0 && (
        <span data-testid="reanalysis-count-badge" title={`${t.reanalysisCount} Neuanalyse${t.reanalysisCount === 1 ? '' : 'n'}`} style={{ ...pill, fontWeight: 600, borderRadius: 8, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 2 }}><ReanalysisIcon size={11} color={theme.colors.navyMid} />{t.reanalysisCount}</span>
      )}
      {t.copyApprovedAt && (
        <span data-testid="kopiert-badge" title="Antwort kopiert, nicht förmlich genehmigt" style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 3, animation: 'stx-fadeIn 0.3s ease' }}>
          <KopiertIcon size={11} color={theme.colors.navyMid} />Kopiert
        </span>
      )}
      {t.edited === true && (
        <span data-testid="bearbeitet-badge" style={{ ...pill, animation: 'stx-fadeIn 0.3s ease' }}>bearbeitet</span>
      )}
      {esk && (
        <span title={`Eskalationsstufe ${eskN}`} style={{ fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap', flexShrink: 0, color: esk.fg, backgroundColor: esk.bg, border: `1px solid ${esk.border}` }}>
          Stufe {eskN}
        </span>
      )}
    </span>
  );

  // Compact (Archiv ledger): one line — badge · reason · 📎 · status (right); the date lives on
  // the caller's subject line, so it is not rendered here.
  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {categoryBadge}
        {pendingChip}
        {secondaryAxisPills}
        {attachment}
        {statusGroup}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {categoryBadge}
        {pendingChip}
        {statusKurzPill}
        {overrideSignalPill}
        {secondaryAxisPills}
        {attachment}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 16, gap: 4 }}>
        <span style={{ fontSize: 11, color: theme.colors.navyMuted, flexShrink: 0 }}>{formatRowDate(dateIso)}</span>
        {statusGroup}
      </div>
    </>
  );
};

// ─── FilterPanel ─────────────────────────────────────────
// <purpose>Reusable search + filter dropdown. Controlled — parent owns all state.</purpose>
// <dependencies>theme, REASON_CODES from constants; useCategoryMeta (role-aware category set)</dependencies>
// <constraints>Click-outside closes dropdown. Live filtering (no apply button). Relative positioning.</constraints>

interface FilterPanelProps {
  search: string;
  onSearchChange: (v: string) => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;

  // Optional sections — rendered only when callback provided
  timeFilter?: string | null;
  onTimeFilterChange?: (v: string | null) => void;
  categoryFilter?: string | null;
  onCategoryFilterChange?: (v: string | null) => void;
  reasonCodeFilter?: string[];
  onReasonCodeFilterChange?: (codes: string[]) => void;
  uploaders?: string[];
  filterUser?: string;
  onFilterUserChange?: (v: string) => void;
  filterDateRange?: string;
  onFilterDateRangeChange?: (v: string) => void;
  dateRangeLabel?: string;

  // Server-side ISO date-range picker (Task 5 / ArchivSeite). Additive: when
  // onDateRangeChange is provided the DateRangeInput renders INSTEAD of the
  // free-text filterDateRange field.
  from?: string | null;
  to?: string | null;
  onDateRangeChange?: (range: DateRangeValue) => void;

  onClearAll: () => void;
  hasActiveFilters: boolean;
}

const TIME_OPTIONS = [
  { label: '7 Tage', value: '7d' },
  { label: '30 Tage', value: '30d' },
  { label: 'Dieser Monat', value: 'thisMonth' },
] as const;

const bubbleStyle = (active: boolean): React.CSSProperties => ({
  padding: '3px 10px', fontSize: 11, fontWeight: active ? 600 : 400,
  color: active ? theme.colors.white : theme.colors.navyMuted,
  backgroundColor: active ? theme.colors.navy : 'transparent',
  border: `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
  borderRadius: 20, cursor: 'pointer', fontFamily: theme.font.family,
  transition: 'all 0.15s ease', outline: 'none', userSelect: 'none' as const,
});

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11, color: theme.colors.textSecondary, display: 'block',
  marginBottom: 6, fontFamily: theme.font.family,
};

const filterInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box' as const,
  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
  fontSize: 13, fontFamily: theme.font.family, outline: 'none',
  backgroundColor: theme.colors.white,
};

export const FilterPanel: React.FC<FilterPanelProps> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { open, onOpenChange } = props;
  const cm = useCategoryMeta();

  // Why: close dropdown when clicking anywhere outside the FilterPanel wrapper
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  const hasDropdownSections = !!(
    props.onTimeFilterChange || props.onCategoryFilterChange ||
    props.onReasonCodeFilterChange || props.onFilterUserChange ||
    props.onFilterDateRangeChange || props.onDateRangeChange
  );

  return (
    <div ref={wrapperRef} style={{ padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>
      {/* Search bar */}
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.bgPage,
        padding: `0 ${theme.spacing.sm}px`,
      }}>
        <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={theme.colors.textSecondary} strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          id="filter-search"
          name="filter-search"
          type="text"
          value={props.search}
          onChange={e => props.onSearchChange(e.target.value)}
          placeholder="Suchen..."
          style={{
            flex: 1, border: 'none', background: 'none', outline: 'none',
            padding: `${theme.spacing.sm}px ${theme.spacing.sm}px`,
            fontSize: 13, fontFamily: theme.font.family, color: theme.colors.navy,
          }}
        />
        {hasDropdownSections && (
          <button
            onClick={() => props.onOpenChange(!props.open)}
            aria-label={props.open ? 'Filter schließen' : 'Filter öffnen'}
            aria-expanded={props.open}
            style={{
              width: 40, height: 40, padding: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (props.open || props.hasActiveFilters) ? 1 : 0.5,
            }}
          >
            <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={props.hasActiveFilters ? theme.colors.navy : theme.colors.textSecondary}
              strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
          </button>
        )}
        {props.hasActiveFilters && (
          <button
            onClick={props.onClearAll}
            aria-label="Alle Filter zurücksetzen"
            style={{
              padding: 4, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, color: theme.colors.textSecondary,
              display: 'flex', alignItems: 'center',
            }}
          >
            &times;
          </button>
        )}
      </div>

      {/* Filter dropdown */}
      {props.open && hasDropdownSections && (
        <div style={{
          marginTop: theme.spacing.xs,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.md,
          boxShadow: theme.shadows.cardHover,
          border: `1px solid ${theme.colors.borderLight}`,
          animation: 'stx-slideIn 0.2s ease',
        }}>
          {/* Time bubbles */}
          {props.onTimeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-zeitraum-label" style={filterLabelStyle}>Zeitraum</span>
              <div role="group" aria-labelledby="filter-zeitraum-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TIME_OPTIONS.map(f => {
                  const active = props.timeFilter === f.value;
                  return (
                    <button key={f.value} style={bubbleStyle(active)}
                      onClick={() => props.onTimeFilterChange!(active ? null : f.value)}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category bubbles — role-aware: VNB marktprozess (abbr chip) / gMSB K-codes */}
          {props.onCategoryFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-kategorie-label" style={filterLabelStyle}>{cm.categoryNoun}</span>
              <div role="group" aria-labelledby="filter-kategorie-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {/* performanceCategoryKeys: the bubble row is the set of categories a clerk can
                    filter WORK by, and it has never offered the unclassified bucket — the pre-vocabulary floor
                    (GMSB_STAT_KEYS) excluded it, so keying to the served marker keeps the row
                    exactly as it is today rather than growing a new bubble the day vocabulary rows
                    land. A dedicated "show me the unclassified" affordance, if it is ever wanted,
                    is a deliberate addition, not a delivery side-effect. */}
                {cm.performanceCategoryKeys.map(cat => {
                  const active = props.categoryFilter === cat;
                  return (
                    <button key={cat} style={bubbleStyle(active)}
                      title={cm.label(cat)}
                      onClick={() => props.onCategoryFilterChange!(active ? null : cat)}>
                      {cm.categoryLabelsFromData ? cm.label(cat) : cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Second-axis multi-select — role-aware: VNB anliegen (schema labels) / gMSB reason codes.
              An interactive filter OPTION (selectable, own active state), not a display pill — it
              reuses the secondary-axis resolution accessors (secondaryAxisText/secondaryAxisHover)
              rather than SecondaryAxisPills, which owns markup/click-handling this affordance keeps
              for itself. categoryLabelsFromData still decides the ID SET (secondaryAxisKeys floors
              to [] with no vocabulary/schema, so gMSB's floor needs REASON_CODES as its option
              source) — only the TEXT formula collapsed to one, since both accessors resolve any
              code regardless of source. */}
          {props.onReasonCodeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-grundcode-label" style={filterLabelStyle}>{cm.secondaryAxisNoun}</span>
              <div role="group" aria-labelledby="filter-grundcode-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(cm.categoryLabelsFromData ? cm.secondaryAxisKeys : REASON_CODES.map(rc => rc.code))
                  .map(id => ({ value: id, text: cm.secondaryAxisText(id), title: cm.secondaryAxisHover(id) }))
                  .map(opt => {
                  const codes = props.reasonCodeFilter ?? [];
                  const active = codes.includes(opt.value);
                  return (
                    <button key={opt.value} style={bubbleStyle(active)}
                      title={opt.title}
                      onClick={() => {
                        const next = active
                          ? codes.filter(c => c !== opt.value)
                          : [...codes, opt.value];
                        props.onReasonCodeFilterChange!(next);
                      }}>
                      {opt.text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Uploader + date range row */}
          {(props.onFilterUserChange || props.onFilterDateRangeChange) && (
            <div style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap' }}>
              {props.onFilterUserChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label htmlFor="filter-uploader" style={filterLabelStyle}>Hochgeladen von</label>
                  <select
                    id="filter-uploader"
                    value={props.filterUser ?? ''}
                    onChange={e => props.onFilterUserChange!(e.target.value)}
                    style={filterInputStyle}
                  >
                    <option value="">Alle</option>
                    {(props.uploaders ?? []).map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Legacy free-text range. Suppressed when the ISO picker is wired. */}
              {props.onFilterDateRangeChange && !props.onDateRangeChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={filterLabelStyle}>{props.dateRangeLabel ?? 'Zeitraum'}</label>
                  <input
                    id="filter-daterange-text"
                    name="filter-daterange-text"
                    type="text"
                    value={props.filterDateRange ?? ''}
                    onChange={e => props.onFilterDateRangeChange!(e.target.value)}
                    placeholder="z.B. 01.01 - 31.01"
                    style={filterInputStyle}
                  />
                </div>
              )}
            </div>
          )}

          {/* ISO date-range picker (ArchivSeite server-side from/to). Own full-width
              block — the native Von/Bis pair needs more room than the free-text field. */}
          {props.onDateRangeChange && (
            <div style={{ marginTop: theme.spacing.sm }}>
              <DateRangeInput
                from={props.from ?? null}
                to={props.to ?? null}
                onChange={props.onDateRangeChange}
                label={props.dateRangeLabel ?? 'Zeitraum'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
